import React from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { useDelayedLoading } from '@/hooks/useDelayedLoading';
import { ListSkeleton } from '@/components/ui/list-skeleton';

interface DelayedSkeletonProps {
	isLoading: boolean;
	delay?: number;
	showInitialLoading?: boolean;
	children: React.ReactNode;
	fallback?: React.ReactNode;
	className?: string;
}

/**
 * A wrapper component that shows skeleton loading with a delay to prevent flashing
 * during quick data fetches. Perfect for details panels and other components that
 * load data frequently.
 */
export const DelayedSkeleton: React.FC<DelayedSkeletonProps> = ({
	isLoading,
	delay = 300,
	showInitialLoading = false,
	children,
	fallback,
	className
}) => {
	const { shouldShowLoading } = useDelayedLoading(isLoading, {
		delay,
		showInitialLoading
	});

	if (shouldShowLoading) {
		if (fallback) {
			return <>{fallback}</>;
		}

		// Default skeleton pattern for details panels
		return (
			<div className={`space-y-4 ${className || ''}`}>
				<div className="space-y-2">
					<Skeleton className="h-4 w-24" />
					<Skeleton className="h-6 w-full" />
				</div>
				<div className="space-y-2">
					<Skeleton className="h-4 w-32" />
					<Skeleton className="h-6 w-3/4" />
				</div>
				<div className="space-y-2">
					<Skeleton className="h-4 w-28" />
					<Skeleton className="h-6 w-2/3" />
				</div>
			</div>
		);
	}

	return <>{children}</>;
};

/**
 * Specific skeleton for details panels with proper spacing and sizing
 */
export const DetailsPanelSkeleton: React.FC<{ className?: string }> = ({
	className
}) => (
	<div className={`space-y-6 p-4 ${className || ''}`}>
		{/* Header section */}
		<div className="space-y-2">
			<Skeleton className="h-3 w-16" />
			<Skeleton className="h-5 w-full" />
		</div>

		{/* Content sections */}
		{Array.from({ length: 3 }, (_, i) => (
			<div key={i} className="space-y-2">
				<Skeleton className="h-3 w-20" />
				<Skeleton className="h-4 w-full" />
			</div>
		))}

		{/* Additional content */}
		<div className="space-y-2">
			<Skeleton className="h-3 w-24" />
			<Skeleton className="h-16 w-full" />
		</div>
	</div>
);

/**
 * Enhanced skeleton for detail views that include lists (like customer details)
 * Shows a realistic loading state with header, fields, and animated list items
 */
export const DetailsWithListsSkeleton: React.FC<{
	className?: string;
	listCount?: number;
	sections?: number;
}> = ({ className, listCount = 3, sections = 3 }) => (
	<div className={`space-y-6 ${className || ''}`}>
		{/* Header section with title */}
		<div className="space-y-2">
			<Skeleton className="h-3 w-16" />
			<Skeleton className="h-6 w-64" />
			<div className="flex gap-2">
				<Skeleton className="h-5 w-20 rounded-full" />
				<Skeleton className="h-5 w-24 rounded-full" />
			</div>
		</div>

		{/* Detail fields */}
		<div className="space-y-2">
			{Array.from({ length: 5 }, (_, i) => (
				<div key={i} className="flex justify-between gap-4">
					<Skeleton className="h-4 w-32" />
					<Skeleton className="h-4 w-40" />
				</div>
			))}
		</div>

		{/* Separator */}
		<Skeleton className="h-px w-full" />

		{/* List sections */}
		{Array.from({ length: sections }, (_, sectionIndex) => (
			<React.Fragment key={sectionIndex}>
				<div className="space-y-3">
					<Skeleton className="h-4 w-36" />
					<ListSkeleton count={listCount} variant="card" />
				</div>
				{sectionIndex < sections - 1 && <Skeleton className="h-px w-full" />}
			</React.Fragment>
		))}
	</div>
);

/**
 * Hook version for more granular control in components
 */
export const useDelayedSkeleton = (
	isLoading: boolean,
	options?: {
		delay?: number;
		showInitialLoading?: boolean;
	}
) => {
	const { shouldShowLoading, hasLoadedOnce, isFirstLoad } = useDelayedLoading(
		isLoading,
		options
	);

	return {
		shouldShowLoading,
		hasLoadedOnce,
		isFirstLoad,
		isLoading,
		/**
		 * Render function that takes children and optional fallback
		 */
		render: (children: React.ReactNode, fallback?: React.ReactNode) => {
			if (shouldShowLoading) {
				return fallback || <DetailsPanelSkeleton />;
			}
			return children;
		}
	};
};
